import { useState } from 'react';
import { X } from 'lucide-react';
import { post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Textarea } from '@/components/atoms/Textarea';
import { useScrollLock } from '@/hooks/useScrollLock';
import { toast } from '@/store/toastStore';

export type ReportTarget = {
  type: 'post' | 'comment';
  id: number;
};

interface ReportModalProps {
  target: ReportTarget | null;
  onClose: () => void;
  onReported?: () => void;
}

const REASONS: Array<{ key: string; label: string }> = [
  { key: 'spam', label: '스팸/도배' },
  { key: 'abuse', label: '욕설/비방' },
  { key: 'misinfo', label: '허위정보' },
  { key: 'illegal-ads', label: '불법 투자 권유' },
  { key: 'etc', label: '기타' },
];

/**
 * 신고 모달 — 게시글/댓글 공통
 * - 유형 선택 라디오 + 선택 사유(기타일 때) + 제출
 * - 서버가 본인/중복/임계 자동 처리
 */
export function ReportModal({ target, onClose, onReported }: ReportModalProps) {
  const [reason, setReason] = useState('spam');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useScrollLock(!!target);

  if (!target) return null;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await post('/reports', {
        targetType: target.type,
        targetId: target.id,
        reason,
        description: description.trim() || undefined,
      });
      toast.success('신고가 접수됐어요.');
      onReported?.();
      onClose();
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      const msg =
        code === 'DUPLICATE_REPORT'
          ? '이미 신고한 항목이에요.'
          : code === 'SELF_REPORT'
          ? '본인 글/댓글은 신고할 수 없어요.'
          : err?.response?.data?.error?.message ?? '신고 처리에 실패했어요.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="section-title">
            {target.type === 'post' ? '게시글' : '댓글'} 신고
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-3 text-sm text-[var(--color-text-sub)]">
          신고 사유를 선택하세요. 누적 신고가 많으면 자동으로 블라인드 또는 삭제 처리돼요.
        </p>
        <div className="mb-4 space-y-1.5">
          {REASONS.map((r) => (
            <label
              key={r.key}
              className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                reason === r.key
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                  : 'border-[var(--color-border)]'
              }`}
            >
              <input
                type="radio"
                name="report-reason"
                value={r.key}
                checked={reason === r.key}
                onChange={() => setReason(r.key)}
                className="h-4 w-4"
              />
              <span>{r.label}</span>
            </label>
          ))}
        </div>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="추가 설명 (선택, 최대 500자)"
          rows={3}
          maxLength={500}
          className="mb-4 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button size="sm" loading={submitting} onClick={submit}>
            신고하기
          </Button>
        </div>
      </div>
    </div>
  );
}
